import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { Instagram, Facebook, Mail, Shield } from 'lucide-react';

const footerNavItems = [
  { key: 'mission',      id: 'mission' },
  { key: 'programs',     id: 'programs' },
  { key: 'testimonials', id: 'testimonials' },
  { key: 'healing',      id: 'crystal-healing' },
  { key: 'about',        id: 'about' },
  { key: 'connect',      id: 'connect' },
];

const Footer = () => {
  const { t, isRTL } = useLanguage();
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const response = await fetch(`${backendUrl}/api/cms/programs`);
        if (response.ok) {
          const data = await response.json();
          setPrograms(data.filter(p => p.status === 'published' || p.status === 'active'));
        }
      } catch (err) {
        console.error('Footer: error fetching programs:', err);
      }
    };
    fetchPrograms();
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  const socialLinks = [
    {
      name: 'Instagram',
      icon: Instagram,
      url: t.footer.social?.instagram || 'https://www.instagram.com/bizzarihealingcrystals'
    },
    {
      name: 'Facebook',
      icon: Facebook,
      url: t.footer.social?.facebook || 'https://www.facebook.com/mona.bizzari'
    }
  ];

  const email = t.connect?.email || 'Mona@monabizzari.com';

  return (
    <footer className="bg-charcoal-500 border-t border-gold/10">
      <div className="container mx-auto px-6 lg:px-10 py-16 lg:py-20">

        {/* Brand header */}
        <div className="text-center mb-14">
          <button
            onClick={() => scrollToSection('hero')}
            className="inline-block mb-3"
          >
            <h3 className="font-script text-3xl lg:text-4xl text-gold hover:text-gold-500 transition-colors">
              {t.brand.name}
            </h3>
          </button>
          <p className="text-cream-400 text-sm tracking-wide max-w-sm mx-auto">
            {t.footer.tagline}
          </p>
          <div className="gold-divider mt-8" />
        </div>

        {/* 3-column link grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-16 mb-14">

          {/* Navigate */}
          <div className={`text-center ${isRTL ? 'md:text-right' : 'md:text-start'}`}>
            <h4 className="text-xs uppercase tracking-widest text-gold mb-5">
              {t.footer.navigation.title}
            </h4>
            <nav className="flex flex-col gap-3">
              {footerNavItems.map((item) => (
                <button
                  key={item.key}
                  onClick={() => scrollToSection(item.id)}
                  className={`text-cream-300 hover:text-gold transition-colors text-sm text-center ${
                    isRTL ? 'md:text-right' : 'md:text-start'
                  }`}
                >
                  {t.nav[item.key]}
                </button>
              ))}
            </nav>
          </div>

          {/* Programs */}
          <div className="text-center">
            <h4 className="text-xs uppercase tracking-widest text-gold mb-5">
              {t.footer.programs.title}
            </h4>
            <nav className="flex flex-col gap-3">
              {programs.length > 0 ? programs.map((program) => (
                <button
                  key={program.slug || program.id}
                  onClick={() => scrollToSection('programs')}
                  className="text-cream-300 hover:text-gold transition-colors text-sm"
                >
                  {isRTL ? (program.arabic_title || program.title) : program.title}
                </button>
              )) : t.footer.programs.items.map((item, index) => (
                <button
                  key={index}
                  onClick={() => scrollToSection('programs')}
                  className="text-cream-300 hover:text-gold transition-colors text-sm"
                >
                  {item}
                </button>
              ))}
            </nav>
          </div>

          {/* Connect — email + social */}
          <div className={`text-center ${isRTL ? 'md:text-left' : 'md:text-end'}`}>
            <h4 className="text-xs uppercase tracking-widest text-gold mb-5">
              {t.footer.connect.title}
            </h4>
            <div className="flex flex-col gap-4 items-center md:items-end">
              {/* Email */}
              <a
                href={`mailto:${email}`}
                className="flex items-center gap-2 text-cream-300 hover:text-gold transition-colors text-sm group"
              >
                <Mail size={13} className="text-gold/70 group-hover:text-gold transition-colors flex-shrink-0" />
                <span>{email}</span>
              </a>
              {/* Social icons */}
              <div className="flex gap-3">
                {socialLinks.map((social, index) => {
                  const Icon = social.icon;
                  return (
                    <a
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-9 h-9 flex items-center justify-center border border-gold/30 text-gold hover:bg-gold hover:text-charcoal transition-all rounded-lg"
                      aria-label={social.name}
                    >
                      <Icon size={15} />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

        </div>

        {/* Bottom bar */}
        <div className="pt-6 border-t border-gold/10 flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="text-cream-400 text-xs order-2 sm:order-1">
            {t.footer.copyright}
          </p>
          <p className="text-cream-400/60 text-xs order-3 sm:order-2 text-center">
            {t.footer.madeWith}
          </p>
          {/* Admin access — subtle, low-profile */}
          <Link
            to="/admin"
            className="flex items-center gap-1.5 text-cream-400/40 hover:text-gold transition-colors text-xs order-1 sm:order-3"
            aria-label="Admin"
          >
            <Shield size={11} />
            <span className="uppercase tracking-wider">{isRTL ? 'لوحة التحكم' : 'Admin'}</span>
          </Link>
        </div>

      </div>
    </footer>
  );
};

export default Footer;
